import React, { useEffect, useState } from "react";
import RawMaterialStockForm from "./raw_materail_form";
import { useDispatch, useSelector } from "react-redux";
import { getProducts } from "../../../slices/company/rawProductSlice";
import { deleteRawMaterial, fetchRawMaterial } from "../../../slices/company/rawMaterialSlice";
import DataTable from "react-data-table-component";
import { Delete, Download, Eye, Pen, Printer, ScanBarcode, Search } from "lucide-react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Modal from "../../../components/modal";
import { API_URL } from "../../../utils/constant";
import { customTableStyles } from "../../../utils/tableStylings";

const RawMaterialStockScreen = () => {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const [modalHeading, setModalHeading] = useState("");
    const [selectedData, setSelectedData] = useState([]);
    const [showVariationModal, setShowVariationModal] = useState(false);
    const [variationList, setVariationList] = useState([]);
    const [variationEdit, setVariationEdit] = useState([]);
    const [variationSearchTerm, setVariationSearchTerm] = useState('');
    const { rawProducts } = useSelector(state => state.rawProduct);
    const { raw_material_stock } = useSelector(state => state.rawMaterialStock);
    const [productsLoading, setProgressPending] = useState(true);

    // Tab and search states
    const [activeTab, setActiveTab] = useState('stock');
    const [searchProductName, setSearchProductName] = useState('');
    const [searchProductCode, setSearchProductCode] = useState('');
    const [searchSerialNo, setSearchSerialNo] = useState('');
    const [searchBarcode, setSearchBarcode] = useState('');
    const [searchUnit, setSearchUnit] = useState('');
    const [searchSupplier, setSearchSupplier] = useState('');

    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            const companyId = company.company?.id;
            setProgressPending(true);
            dispatch(fetchRawMaterial(companyId)).finally(() => {
                setProgressPending(false);
            });
            dispatch(getProducts(companyId));
        }
    }, [dispatch]);


    const handleAdd = () => {
        setSelectedData([]);
        setModalHeading('Create Stock'); // No supplier means we are adding a new one
        setShowModal(true);
    };

    const handleVariationChange = (id, field, value) => {
        setVariationEdit(prev => prev.map((v) => v.id === id ? { ...v, [field]: value } : v));
    };

    const filteredVariations = variationEdit.filter(variation =>
        variation.variant_name?.toLowerCase().includes(variationSearchTerm.toLowerCase()) ||
        variation.barcode?.toLowerCase().includes(variationSearchTerm.toLowerCase()) ||
        variation.serial_no?.toLowerCase().includes(variationSearchTerm.toLowerCase())
    );

    // Filter stock by each field
    const filteredStock = raw_material_stock.filter(row => {
        if (searchProductName && !(row.product_name && row.product_name.toLowerCase().includes(searchProductName.toLowerCase()))) {
            return false;
        }
        if (searchProductCode && !(row.product_code && row.product_code.toLowerCase().includes(searchProductCode.toLowerCase()))) {
            return false;
        }
        if (searchUnit && !(row.unit && row.unit.toLowerCase().includes(searchUnit.toLowerCase()))) {
            return false;
        }
        if (searchSupplier && !(row.supplier?.name && row.supplier.name.toLowerCase().includes(searchSupplier.toLowerCase()))) {
            return false;
        }
        // Serial No and Barcode in variantStocks
        if (searchSerialNo || searchBarcode) {
            const matchesVariation = row.variantStocks && row.variantStocks.some(v => (
                (!searchSerialNo || (v.serial_no && v.serial_no.toLowerCase().includes(searchSerialNo.toLowerCase()))) &&
                (!searchBarcode || (v.barcode && v.barcode.toLowerCase().includes(searchBarcode.toLowerCase())))
            ));
            if (!matchesVariation) return false;
        }
        return true;
    });
    // Gather all variants for Variants tab
    const allVariants = raw_material_stock.flatMap(row => (row.variantStocks || []).map(v => ({ ...v, stock: row })));
    const filteredVariants = allVariants.filter(variation => {
        return (
            (!searchProductName || (variation.stock?.product_name && variation.stock.product_name.toLowerCase().includes(searchProductName.toLowerCase()))) &&
            (!searchProductCode || (variation.stock?.product_code && variation.stock.product_code.toLowerCase().includes(searchProductCode.toLowerCase()))) &&
            (!searchUnit || (variation.stock?.unit && variation.stock.unit.toLowerCase().includes(searchUnit.toLowerCase()))) &&
            (!searchSupplier || (variation.stock?.supplier?.name && variation.stock.supplier.name.toLowerCase().includes(searchSupplier.toLowerCase()))) &&
            (!searchSerialNo || (variation.serial_no && variation.serial_no.toLowerCase().includes(searchSerialNo.toLowerCase()))) &&
            (!searchBarcode || (variation.barcode && variation.barcode.toLowerCase().includes(searchBarcode.toLowerCase())))
        );
    });

    const handleDownloadBarcode = (barcodeImage, barcode) => {
        if (barcodeImage) {
            const link = document.createElement('a');
            link.href = `${API_URL}${barcodeImage}`;
            link.download = `barcode_${barcode}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Define columns for DataTable
    const columns = [
        {
            name: '#',
            cell: (row, index) => index + 1,
            width: '40px' // Keep fixed width for serial numbers
        },
        {
            name: 'Product Name',
            selector: row => row.product_name, // Adjust this according to your data structure
            sortable: true,
        },

        {
            name: 'Product Code / Article No / Article Name',
            selector: row => row.product_code,
            sortable: true,
        },
        {
            name: 'Brand',
            selector: row => row.products?.Brand?.name || 'OTHER', // Adjust this according to your data structure
            sortable: true,
        },

        {
            name: 'Quantity',
            selector: row => row.products?.product_type == 'variants' ? row.variantStocks?.length || 0 : row.quantity || 0,
            sortable: true,
        },
        {
            name: 'Unit',
            selector: row => row.unit,
            sortable: true,
        },
        {
            name: 'Purchase Date',
            selector: row => row.purchase_date ? new Date(row.purchase_date).toLocaleDateString() : 'N/A',
            sortable: true,
        },
        {
            name: 'Vendor/Supplier',
            selector: row => row.supplier?.name || "",
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row) => (
                <>
                    {row.variantStocks.length > 0 && Array.isArray(row.variantStocks) && (
                        <span
                            className="btn btn-success btn-sm text-white me-2"
                            title="View Variations"
                            onClick={() => handleViewVariations(row)}
                        >
                            <Eye />
                        </span>
                    )}
                    <button className="btn btn-warning btn-sm text-white me-2" onClick={() => handleEdit(row)}><Pen /></button>
                    <button className="btn btn-danger btn-sm text-white" onClick={() => handleDelete(row.id)}><Delete /></button>
                </> // Handle edit functionality here
            ),
        },
    ];

    const handleViewVariations = (product) => {
        // If product_type is variant and product.variations exists, show modal
        if (product.variantStocks.length > 0 && Array.isArray(product.variantStocks)) {
            setVariationList(product.variantStocks.map(v => ({ ...v, stock: product })));
            setVariationEdit(product.variantStocks.map(v => ({ ...v, stock: product })));
            setVariationSearchTerm(''); // Clear search term when opening modal

            console.log('Product Variations:', product.variantStocks);
            setShowVariationModal(true);
        }
    };


    const handleCloseVariationModal = () => {
        setShowVariationModal(false);
        setVariationSearchTerm(''); // Clear search term when closing modal
    };

    // Columns configuration for variations datatable
    const variationsColumns = [
        {
            name: '#',
            cell: (row, index) => index + 1,
            width: '60px',
            sortable: true
        },
        {
            name: 'Image',
            cell: row => (
                <img
                    src={`${API_URL}${row.image}`}
                    alt={row.name}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = './logo.png'; // fallback logo on load error
                    }}
                    style={{ height: '50px', width: "50px", objectFit: 'cover' }}
                />
            ),
            width: '60px',
            sortable: true
        },
        {
            name: 'Product Name',
            cell: row => (
                <div>
                    <strong>{row.stock?.product_name || row.product?.name || ''}</strong>

                </div>
            ),
            sortable: true,
        },
        {
            name: 'Article Number',
            cell: row => (
                <div>
                    <div className="text-muted small">{row.stock?.product_code || row.product?.sku || ''}</div>

                </div>
            ),
            sortable: true
        },
        {
            name: 'Variation Name',
            selector: row => row.variant_name,
            sortable: true,
        },
        {
            name: 'Price',
            cell: row => row.price,
        },
        {
            name: 'Barcode',
            selector: row => row.barcode,
            sortable: true,
            cell: row => (
                <div className="text-truncate" title={row.barcode}>
                    {row.barcode}
                </div>
            )
        },
        {
            name: 'Barcode Image',
            cell: row => (
                row.barcode_image ? (
                    <img
                        src={`${API_URL}${row.barcode_image}`}
                        alt={row.barcode}
                        style={{ height: '40px', width: 'auto' }}
                        className="img-thumbnail"
                    />
                ) : (
                    <span className="text-muted small">No Image</span>
                )
            ),
            width: '100px'
        },
        {
            name: 'Serial No',
            cell: row => row.serial_no,
            sortable: true
        },
        {
            name: 'Segment',
            selector: row => row.segment,
            sortable: true
        },

        {
            name: 'Sub-Segment',
            selector: row => row.subSegment,
            sortable: true
        },
    ];

    const handlePrintBarcode = (barcodeImage, barcode) => {
        if (barcodeImage) {
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                    <head>
                        <title>Print Barcode - ${barcode}</title>
                        <style>
                            body { margin: 0; padding: 20px; text-align: center; }
                            .barcode-container { margin: 20px; }
                            .barcode-label { margin-top: 10px; font-size: 14px; }
                        </style>
                    </head>
                    <body>
                        <div className="barcode-container">
                            <img src="${API_URL}${barcodeImage}" alt="${barcode}" style="max-width: 100%; height: auto;" />
                            <div className="barcode-label">${barcode}</div>
                        </div>
                    </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        }
    };

    const handleDelete = (taxId) => {
        Swal.fire({
            title: '<strong>Are you sure?</strong>',
            html: `<p>This action will <b>permanently delete</b> this Stock.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteRawMaterial(taxId));
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'The Stock has been deleted.',
                    toast: true,          // Enable toast style
                    position: 'top-end',  // Position at the top-right corner
                    showConfirmButton: false,  // Hide the confirm button
                    timer: 3000,          // Set the timer to auto-close after 3 seconds
                    timerProgressBar: true,  // Show the progress bar for the timer
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer);
                        toast.addEventListener('mouseleave', Swal.resumeTimer);
                    }
                });

            }
        });

    };

    const handleEdit = (row) => {
        setSelectedData(row);
        setModalHeading('Edit Stock');
        setShowModal(true);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">

            </div>
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h3 className="text-primary mb-0">Item Purchased Stock</h3>
                    <div className="d-flex gap-2 flex-wrap">
                        <Link className="btn btn-success" to={`/scanner`}>
                            <ScanBarcode /> Scan Barcode
                        </Link>
                        <button className="btn btn-primary" onClick={handleAdd}>
                            + Add Stock
                        </button>
                    </div>
                </div>
                <div className="card-header">
                    <h6 className="mb-0">Search Stock</h6>
                    <div className="row g-2 align-items-end">
                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                            <div className="position-relative mt-3 mb-2">
                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Product Name
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Product Name"
                                    value={searchProductName}
                                    onChange={e => setSearchProductName(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                            <div className="position-relative mt-3 mb-2">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Product Code / Article No
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Product Code / Article No"
                                    value={searchProductCode}
                                    onChange={e => setSearchProductCode(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <div className="position-relative mt-3 mb-2">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Serial No
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Serial No"
                                    value={searchSerialNo}
                                    onChange={e => setSearchSerialNo(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <div className="position-relative mt-3 mb-2">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Barcode
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Barcode"
                                    value={searchBarcode}
                                    onChange={e => setSearchBarcode(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <div className="position-relative mt-3 mb-2">

                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Supplier
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Supplier"
                                    value={searchSupplier}
                                    onChange={e => setSearchSupplier(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <ul className="nav nav-pills mb-3 nav-justified tab-style-5 d-flex flex-wrap gap-2" role="tablist">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'stock' ? 'active' : ''}`}
                                onClick={() => setActiveTab('stock')}
                                type="button"
                            >
                                Stock
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'variants' ? 'active' : ''}`}
                                onClick={() => setActiveTab('variants')}
                                type="button"
                            >
                                Variants
                            </button>
                        </li>
                    </ul>
                    {activeTab === 'stock' && (
                        <DataTable
                            columns={columns}
                            data={filteredStock}
                            pagination
                            highlightOnHover
                            striped
                            dense
                            progressPending={productsLoading}
                            customStyles={customTableStyles}
                        />
                    )}
                    {activeTab === 'variants' && (
                        <DataTable
                            columns={variationsColumns}
                            data={filteredVariants}
                            pagination
                            highlightOnHover
                            striped
                            dense
                            progressPending={productsLoading}
                            customStyles={customTableStyles}
                        />
                    )}
                </div>
            </div>
            <RawMaterialStockForm
                show={showModal}
                onClose={() => setShowModal(false)}
                heading={modalHeading}
                initialData={selectedData}
                rawProducts={rawProducts}
            />
            {showVariationModal && (
                <Modal show={showVariationModal} onClose={handleCloseVariationModal} heading="Product Variations" size="lg">
                    {/* Search Bar */}
                    <div className="mb-3">
                        <div className="input-group">
                            <span className="input-group-text">
                                <Search /> {/* Search Icon */}
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by variation name, barcode, or serial number..."
                                value={variationSearchTerm}
                                onChange={(e) => setVariationSearchTerm(e.target.value)}
                            />
                            {variationSearchTerm && (
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => setVariationSearchTerm('')}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <DataTable
                            columns={variationsColumns}
                            data={filteredVariations}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            highlightOnHover
                            striped
                            dense
                            responsive={false}
                            noDataComponent={
                                variationSearchTerm ?
                                    `No variations found matching "${variationSearchTerm}"` :
                                    "No variations found"
                            }
                            customStyles={customTableStyles}
                        />
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted small">
                            Showing {filteredVariations.length} of {variationEdit.length} variations
                        </div>
                        <div>
                            <button className="btn btn-secondary me-2" onClick={handleCloseVariationModal}>Close</button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default RawMaterialStockScreen;
